// components/SelectorModal.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Input,
  Space,
  Button,
  message,
  Tag,
  Typography
} from 'antd';
import {
  SearchOutlined,
  CloseOutlined,
  CheckOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';

const { Text } = Typography;

const SelectorModal = ({
  visible,
  onClose,
  onSelect,
  title = 'Seleccionar',
  endpoint,
  columns = [],
  searchFields = ['Codigo', 'Nombre'],
  extraParams = {},
  rowKey = 'Codigo',
  pageSize = 10,
  width = 800,
  showSearch = true,
  searchPlaceholder = 'Buscar por código o nombre...',
  emptyText = 'No se encontraron resultados',
  // Nuevas props para personalización
  dataKey = 'data', // La clave donde están los datos en la respuesta
  paginationKey = 'pagination', // La clave donde está la paginación
  searchParam = 'search', // El nombre del parámetro de búsqueda
  renderItem = null, // Función para renderizar cada item en el modal
  showActionColumn = true, // Mostrar columna de acción
  actionText = 'Seleccionar',
  onRowDoubleClick = null, // Función para doble click en fila
  // Props para formateo de datos
  formatData = null, // Función para formatear los datos antes de mostrarlos
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: pageSize,
    total: 0
  });

  useEffect(() => {
    if (visible) {
      loadData(1, searchText);
    }
  }, [visible]);

  const loadData = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page,
        pageSize: pagination.pageSize,
        ...(search && { [searchParam]: search }),
        ...extraParams
      });

      const response = await axios.get(
        `${BASE_URL}${endpoint}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Obtener los datos de la respuesta
      let items = [];
      if (response.data && typeof response.data === 'object') {
        // Si la respuesta tiene la estructura { data: [...] }
        if (response.data[dataKey]) {
          items = response.data[dataKey];
        } else if (Array.isArray(response.data)) {
          items = response.data;
        } else {
          items = response.data.data || response.data || [];
        }
      } else {
        items = response.data || [];
      }

      // Aplicar formato si existe
      if (formatData) {
        items = items.map(formatData);
      }

      setData(items);
      setFilteredData(items);

      // Obtener paginación
      let paginationData = {};
      if (response.data && response.data[paginationKey]) {
        paginationData = response.data[paginationKey];
      } else if (response.data && response.data.pagination) {
        paginationData = response.data.pagination;
      }

      setPagination({
        current: paginationData.currentPage || paginationData.page || page,
        pageSize: paginationData.pageSize || pagination.pageSize,
        total: paginationData.totalRecords || paginationData.total || items.length
      });

    } catch (error) {
      console.error('Error cargando datos:', error);
      message.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    loadData(1, value);
  };

  const handleTableChange = (newPagination) => {
    loadData(newPagination.current, searchText);
  };

  const handleRowDoubleClick = (record) => {
    if (onRowDoubleClick) {
      onRowDoubleClick(record);
    } else if (onSelect) {
      onSelect(record);
      onClose();
    }
  };

  // Columnas por defecto
  const defaultColumns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Nombre',
      dataIndex: 'Nombre',
      key: 'Nombre',
      ellipsis: true
    },
    ...(showActionColumn ? [{
      title: 'Acción',
      key: 'accion',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          onClick={() => {
            onSelect(record);
            onClose();
          }}
        >
          {actionText}
        </Button>
      )
    }] : [])
  ];

  // Si se proporcionan columnas, agregar la columna de acción al final
  const finalColumns = columns.length > 0 ? [
    ...columns,
    ...(showActionColumn ? [{
      title: 'Acción',
      key: 'accion',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          onClick={() => {
            onSelect(record);
            onClose();
          }}
        >
          {actionText}
        </Button>
      )
    }] : [])
  ] : defaultColumns;

  // Renderizar contenido personalizado si se proporciona
  const renderContent = () => {
    if (renderItem) {
      return (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '8px',
          maxHeight: '400px',
          overflowY: 'auto',
          padding: '8px'
        }}>
          {filteredData.map((item, index) => (
            <div 
              key={item[rowKey] || index}
              onClick={() => {
                onSelect(item);
                onClose();
              }}
              style={{ cursor: 'pointer' }}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      );
    }

    return (
      <Table
        columns={finalColumns}
        dataSource={filteredData}
        rowKey={rowKey}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="small"
        locale={{ emptyText }}
        scroll={{ x: true }}
        onRow={(record) => ({
          onDoubleClick: () => handleRowDoubleClick(record),
          style: { cursor: onRowDoubleClick || onSelect ? 'pointer' : 'default' }
        })}
      />
    );
  };

  return (
    <Modal
      title={
        <Space>
          <SearchOutlined />
          <span>{title}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={width}
      footer={[
        <Button key="close" onClick={onClose} icon={<CloseOutlined />}>
          Cerrar
        </Button>
      ]}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {showSearch && (
          <Input.Search
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
            allowClear
            enterButton
          />
        )}

        {renderContent()}
      </Space>
    </Modal>
  );
};

export default SelectorModal;